import { useCallback, useRef } from 'react';

import { Fragment, Slice, Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { DecorationSet, EditorView } from '@atlaskit/editor-prosemirror/view';

import { Positions } from '../../../ui/utils/xstate/get-ai-experience-service';
import { createAIResponseDecoration, getHiddenRangeDecorations } from '../actions';
import { inEditorUserContentDecorationPluginKey } from '../in-editor-pm-plugins/in-editor-user-content-decoration/in-editor-user-content-decoration-plugin-key';
import { createCommand } from '../in-editor-streaming-plugin-factory';

function getLastContentDepth(fragment: Fragment) {
	let depth = 0;
	let currentNode: Fragment | PMNode = fragment;

	while (currentNode.lastChild) {
		depth++;
		currentNode = currentNode.lastChild;
	}

	return depth;
}

export const useInEditorStreaming = ({
	view,
	positions,
}: {
	view: EditorView;
	positions: Positions;
}) => {
	const innerEditorViewRef = useRef<EditorView>();
	const [startPos, endPos] = positions;
	const originalContentRef = useRef<Slice>();
	const offsetRef = useRef<number>();

	const mountDecorations = useCallback(() => {
		const decorations = getHiddenRangeDecorations({
			state: view.state,
			initialPositions: [startPos, endPos],
		});
		const { widget, innerEditorView, originalContent, offset } = createAIResponseDecoration(
			[startPos, endPos],
			view.state,
		);

		innerEditorViewRef.current = innerEditorView;
		originalContentRef.current = originalContent;
		offsetRef.current = offset;

		const decorationSet = DecorationSet.create(view.state.doc, [...decorations, widget]);
		const command = createCommand({
			type: 'START',
			data: {
				innerEditorView,
				decorationSet,
			},
		});

		command(view.state, view.dispatch);
	}, [view, startPos, endPos]);

	const updateStream = useCallback(
		(frag: Fragment) => {
			if (!innerEditorViewRef.current || !originalContentRef.current) {
				return;
			}

			/**
			 * We need to re-create the fragment to make it compatible with the inner editor view's schema
			 * Otherwise it will not update the document and fail silently
			 * We recreate it as a slice so that it can be inserted inline.
			 */

			const recreatedFragment = Fragment.fromJSON(
				innerEditorViewRef.current.state.schema,
				frag.toJSON(),
			);
			const lastFragmentDepth = getLastContentDepth(recreatedFragment);

			/** TODO: Revisit slice depth logic - logic based on
			 *  platform/packages/editor/editor-plugin-ai/src/ui/prebuilt/config-items/utils/action-utils.ts
			 */

			const originalContentFrag = originalContentRef.current.content;

			const recreatedOriginalContent = Fragment.fromJSON(
				innerEditorViewRef.current.state.schema,
				originalContentFrag.toJSON(),
			);
			const endPositionDepth = getLastContentDepth(recreatedOriginalContent);
			const sliceOpenEnd = Math.min(endPositionDepth, lastFragmentDepth);
			let recreatedSlice = new Slice(recreatedFragment, 1, sliceOpenEnd);
			/**
			 * Revisit later - occasionally we receive a response with empty content which causes the fragment -> slice
			 * to have a negative size, which causes the experience to break.
			 * At the moment, we can fallback to empty slice but user clicking 'replace' won't see any changes.
			 */
			if (recreatedSlice.size < 0) {
				recreatedSlice = Slice.empty;
			}

			// check view selection from/to parent, and only break up first / last nodes if it's the same
			if (innerEditorViewRef.current) {
				let tr = innerEditorViewRef.current.state.tr;
				tr = tr.replaceWith(
					0,
					innerEditorViewRef.current.state.doc.content.size,
					recreatedOriginalContent,
				);

				if (offsetRef.current !== undefined) {
					const offsetStartPos = offsetRef.current;
					const offsetEndPos = offsetStartPos + endPos - startPos;

					tr = tr.replace(
						offsetStartPos,
						Math.min(offsetEndPos, offsetStartPos + recreatedSlice.size),
						recreatedSlice,
					);
					tr.setMeta(inEditorUserContentDecorationPluginKey, {
						action: 'updatePositions',
						selectionPositions: [offsetStartPos, offsetEndPos],
						aiContentPositions: [offsetStartPos, offsetStartPos + recreatedSlice.size],
						userContentPositions: [offsetStartPos + recreatedSlice.size, offsetEndPos],
					});
				}

				innerEditorViewRef.current.dispatch(tr);
			}
		},
		[innerEditorViewRef, startPos, endPos],
	);

	return {
		mountDecorations,
		updateStream,
	};
};
